/** Convert display name to DB column key: lowercase, spaces/special chars → underscores, no leading/trailing underscores */
export const getJsonFieldName = (str) => {
  if (!str) return "";
  let jsonFieldName = str.trim().replace(/[^\w\s]/gi, " ").replace(/\s+/g, "_").toLowerCase();
  jsonFieldName = jsonFieldName.replace(/(?:__+)/g, "_");
  jsonFieldName = jsonFieldName.replace(/^_+|_+$/g, "");
  return jsonFieldName;
};

const PRODUCT_FIXED_FIELD_MAP = [
  { formKey: "sample_type", json_field: "sample_type" },
  { formKey: "date", json_field: "received_date" },
  { formKey: "physical_category", json_field: "physical_category" },
];

const ENVIRONMENT_FIXED_FIELD_MAP = [
  { formKey: "sample_type", json_field: "sample_type" },
  { formKey: "date", json_field: "received_date" },
  { formKey: "physical_category", json_field: "physical_category" },
  { formKey: "swab_number", json_field: "swab_number" },
  { formKey: "zone", json_field: "zone" },
  { formKey: "section", json_field: "section" },
];

const buildNonEditableFields = (fieldMap, info = {}) => fieldMap.map(({ formKey, json_field }) => ({
  json_field,
  title_field: info[formKey],
}));

/**
 * Build product fixed fields payload from form state (maps date → received_date).
 * @param {Object} productInfo product_info from fields form state
 * @returns {Array<{json_field, title_field}>}
 */
export const buildProductNonEditableFields = (productInfo = {}) => (
  buildNonEditableFields(PRODUCT_FIXED_FIELD_MAP, productInfo)
);

/**
 * Build environment fixed fields payload from form state (maps date → received_date).
 * @param {Object} environmentInfo environment_info from fields form state
 * @returns {Array<{json_field, title_field}>}
 */
export const buildEnvironmentNonEditableFields = (environmentInfo = {}) => (
  buildNonEditableFields(ENVIRONMENT_FIXED_FIELD_MAP, environmentInfo)
);

const toLinkArray = (link) => {
  if (link == null || link === "") return [];
  return [].concat(link);
};

const withValidFields = (fields = []) => fields.filter((field) => field?.json_field && field?.title_field);

const buildAddressPayload = (address = {}) => ({
  city: address.city ?? "",
  country: address.country ?? "",
  line_2: address.line_2 ?? "",
  state: address.state ?? "",
  street: address.street ?? "",
  zipcode: address.zipcode ?? "",
});

/**
 * Product sample payload for a location. Empty object when sampling is not enabled.
 * @param {Object} productInfo product_info from fields form or extra location
 * @returns {Object}
 */
export const buildProductInfoPayload = (productInfo = {}) => {
  if (productInfo.sample_supply !== "Yes") return {};
  return {
    fields: withValidFields([
      ...buildProductNonEditableFields(productInfo),
      ...(productInfo.editable_fields ?? []),
    ]),
    product_link: toLinkArray(productInfo.product_link),
  };
};

/**
 * Environment sample payload for a location. Empty object when sampling is not enabled.
 * @param {Object} environmentInfo environment_info from fields form or extra location
 * @returns {Object}
 */
export const buildEnvironmentInfoPayload = (environmentInfo = {}) => {
  if (environmentInfo.env_sample_supply !== "Yes") return {};
  return {
    fields: withValidFields([
      ...buildEnvironmentNonEditableFields(environmentInfo),
      ...(environmentInfo.editable_fields ?? []),
    ]),
    environment_link: toLinkArray(environmentInfo.environment_link),
    environment_supplies_needed: environmentInfo.environment_supplies_needed,
  };
};

const buildContactPayload = ({
  address = {},
  billingAddress,
  contactSource = {},
  sameAsAddress = false,
}) => {
  const locAddress = buildAddressPayload(address);
  const billing = contactSource.billing || {};
  const billingAddr = sameAsAddress
    ? locAddress
    : buildAddressPayload(billingAddress ?? billing.address);
  // Field order matches usersignuponboard add-company contract
  return {
    contact_name: contactSource.contact_name ?? "",
    position: contactSource.position ?? "",
    email: contactSource.email ?? "",
    fax: contactSource.fax ?? "",
    phone_number: contactSource.phone_number ?? "",
    address: locAddress,
    billing: {
      bill_to: billing.bill_to ?? "",
      ap_contact: billing.ap_contact ?? "",
      email: billing.email ?? "",
      phone_number: billing.phone_number ?? "",
      address: billingAddr,
    },
  };
};

/**
 * Build top-level users[] for usersignuponboard from the signup / account form.
 * @param {Object} signup
 * @returns {Array<Object>}
 */
export const buildOnboardingUsers = (signup = {}) => {
  if (!signup.email) return [];
  const user = {
    email: signup.email,
    company_name: signup.company_name ?? "",
    customer_name: signup.customer_name ?? "",
    phone_number: signup.phone_number ?? "",
  };
  if (signup.password) {
    user.password = signup.password;
  }
  return [user];
};

/** Split a trailing zip token (any word containing a digit) off the end of a string. */
const splitTrailingZip = (text = "") => {
  const match = text.trim().match(/^(.*?)\s+([A-Za-z0-9-]*\d[A-Za-z0-9-]*)$/);
  return match ? [match[1].trim(), match[2]] : [text.trim(), ""];
};

/**
 * Parse customerinfo address_2 into city / state / zipcode / country.
 * Accepts "City, State Zip, Country" (customerinfo) and "City, State, Country Zip".
 * @param {String} address2
 * @returns {Object} { city, state, zipcode, country }
 */
export const parseCityStateZipCountry = (address2 = "") => {
  const parts = address2.split(",").map((part) => part.trim()).filter(Boolean);
  const [city = "", middle = "", last = ""] = parts;
  const [middleText, middleZip] = splitTrailingZip(middle);
  if (middleZip) {
    return {
      city, state: middleText, zipcode: middleZip, country: last,
    };
  }
  const [lastText, lastZip] = splitTrailingZip(last);
  return {
    city, state: middle, zipcode: lastZip, country: lastText,
  };
};

/**
 * Parse customerinfo address_1 / address_2 strings into form address shape.
 * Used by Add Company "Manage under existing account" and "same as current company".
 * @param {Object} customerInfo getCustomerInfo result
 * @returns {Object} { street, line_2, zipcode, city, state, country }
 */
export const parseCustomerInfoLocationAddress = (customerInfo = {}) => {
  const {
    city, state, zipcode, country,
  } = parseCityStateZipCountry(customerInfo.address_2 || "");
  const [street = "", ...line2Parts] = (customerInfo.address_1 || "")
    .split(",")
    .map((part) => part.trim());
  const line_2 = line2Parts.filter(Boolean).join(", ");

  // Structured location address if API provides it
  const structured = customerInfo.location_address || customerInfo.address || {};
  return {
    street: structured.street_address || structured.street || street,
    line_2: structured.street_address_2 || structured.line_2 || line_2,
    zipcode: structured.zip_code || structured.zipcode || zipcode,
    city: structured.city || city,
    state: structured.state_or_province || structured.state || state,
    country: structured.country || country,
  };
};

/**
 * Map a customerinvitation location_address object into the onboarding address shape.
 * @param {Object} locationAddress
 * @returns {Object|null}
 */
export const mapInvitationLocationAddress = (locationAddress) => {
  if (!locationAddress || typeof locationAddress !== "object" || Array.isArray(locationAddress)) {
    return null;
  }
  const mapped = {
    street: locationAddress.street_address || "",
    line_2: locationAddress.street_address_2 || "",
    zipcode: locationAddress.zip_code || "",
    city: locationAddress.city || "",
    country: locationAddress.country || "",
    state: locationAddress.state_or_province || "",
  };
  const hasAddress = Object.values(mapped).some((value) => String(value || "").trim());
  return hasAddress ? mapped : null;
};

const invitationAddressEntries = (item = {}) => {
  const raw = item.location_address;
  if (Array.isArray(raw)) {
    return raw.filter((entry) => entry && typeof entry === "object" && !Array.isArray(entry));
  }
  if (raw && typeof raw === "object") return [raw];
  return [];
};

/**
 * Turn GET /customerinvitation/ result[] into onboarding locations.
 * Each result row is a location. A row may also carry location_address as an array.
 * @param {Array<Object>} result
 * @param {Object} fallbacks shared customer name/email
 * @returns {Array<Object>}
 */
export const buildInvitationLocations = (result = [], fallbacks = {}) => {
  const customerName = fallbacks.customerName || "";
  const customerEmail = fallbacks.customerEmail || "";
  const locations = [];

  result.forEach((item) => {
    const entries = invitationAddressEntries(item);
    const sources = entries.length ? entries : [null];
    sources.forEach((entry) => {
      const address = mapInvitationLocationAddress(entry) || {
        street: "",
        line_2: "",
        zipcode: "",
        city: "",
        country: "",
        state: "",
      };
      const hasAddress = Object.values(address).some((value) => String(value || "").trim());
      const locationName = entry?.location_name || "";
      const companyName = sources.length > 1 && locationName
        ? [item?.company_name, locationName].filter(Boolean).join(" - ")
        : (item?.company_name || locationName || "");
      if (!hasAddress && !companyName) return;

      const index = locations.length;
      const isPrimary = index === 0;
      const email = item?.customer_email || customerEmail || "";
      const name = companyName || `Location ${index + 1}`;
      locations.push({
        id: `invitation-loc-${index}`,
        name,
        location_name: locationName,
        address,
        billingAddress: null,
        // Secondary invitation locations must be walked through by the user
        ...(isPrimary ? {} : { pendingCompletion: true }),
        hasProduct: false,
        hasEnvironment: false,
        ...(index === 0 ? {} : { managementType: "existing" }),
        company_name: companyName,
        customer_name: item?.customer_name || customerName,
        customer_email: email,
        distribution_list: [email].filter(Boolean),
        seededFromInvitation: true,
      });
    });
  });

  return locations;
};

/**
 * Resolve independent_flag for the currently selected company from useraccounts/.
 * @param {Array<Object>} companyList result[] from GET /useraccounts/
 * @param {String} companyName currently selected company_name
 * @returns {boolean}
 */
export const getCompanyIndependentFlag = (companyList, companyName) => (
  companyList?.find(
    (company) => company.company_name?.trim() === companyName?.trim(),
  )?.independent_flag === true
);

/**
 * Always send locations[]: one item for a single site, N items when more sites were added.
 * independent_flag: independently managed locations are true.
 * Primary (add-company and invite) follows the user's existing vs independent choice.
 * Secondary locations that share the parent account are false.
 * Signup credentials live in top-level users[]; each location only sends admin_email.
 * @param {Object} params
 * @returns {Array<Object>}
 */
export const buildOnboardingLocations = ({
  locations = [],
  contact = {},
  companyInfo = {},
  productInfo = {},
  environmentInfo = {},
  signup = {},
  customerId = "",
  sameAsAddress = false,
  primaryManagementType = null,
}) => {
  const sourceLocations = locations.length
    ? locations
    : [{
      id: "primary",
      managementType: primaryManagementType,
      company_name: companyInfo.company_name,
      distribution_list: companyInfo.distribution_list,
      address: contact.address,
      billingAddress: contact.billing?.address,
    }];

  return sourceLocations.map((loc, index) => {
    const isPrimary = index === 0;
    const managementType = loc.managementType ?? (isPrimary ? primaryManagementType : undefined);
    const contactSource = isPrimary ? contact : (loc.contact_info ?? {});

    return {
      independent_flag: managementType === "independent",
      customer_id: isPrimary ? (customerId ?? "") : "",
      admin_email: signup.email ?? "",
      company_info: {
        company_name: isPrimary
          ? (companyInfo.company_name ?? "")
          : (loc.company_name ?? ""),
        distribution_list: isPrimary
          ? (companyInfo.distribution_list ?? [])
          : (loc.distribution_list ?? []),
      },
      contact: buildContactPayload({
        address: isPrimary ? contact.address : loc.address,
        billingAddress: isPrimary ? undefined : loc.billingAddress,
        contactSource,
        sameAsAddress: isPrimary
          ? sameAsAddress
          : Boolean(loc.contact_info?.same_as_address),
      }),
      product_info: isPrimary
        ? productInfo
        : buildProductInfoPayload(loc.product_info),
      environment_info: isPrimary
        ? environmentInfo
        : buildEnvironmentInfoPayload(loc.environment_info),
    };
  });
};

/**
 * Parse email list string into array of emails
 * @param {String} emailListStr comma or semicolon separated list of emails
 * @returns {Array} array of emails
 */
export const parseDistributionList = (emailListStr) => {
  if (emailListStr?.trim()) {
    return emailListStr.trim().split(/[;|,|\s]+/g); // split by any number of consecutive commas, semicolons, and spaces
  }
  return [];
};
